import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import VSNBreadcrumbs from "../../shared/VSNBreadcrumbs";
import VSNTypography from "../../shared/VSNTypography";
import { LearningStyle } from "./LearningStyle";
import VSNPaper from "../../shared/VSNPaper";
import { getContentTypeDetails } from "./LearningConstant";
import learningExploreAllContainer from "../../container/learningExploreAll.container";
import VSNLoader from "../../shared/VSNLoader";
import VSNProgressiveImage from "../../shared/VSNProgressiveImage";
import videoImage from "../../assets/learning/video.webp";
import { capitalizeFirstLetter } from "../../utils/javascript";

const durations = ["<5 mins", "<10 mins", "<15 mins"];
const sortOptions = ["Last Added", "A - Z", "Z - A"];
const getMinutes = (duration) => parseInt(duration, 10);

const ExploreAll = () => {
  const classes = LearningStyle();
  const navigate = useNavigate();
  const {
    exploreAllData = [],
    exploreAllDataLoader,
    categoriesName,
    from,
  } = learningExploreAllContainer();
  const breadcrumbsRoutes = [
    {
      path: `/learning?from=${
        from === "categorys"
          ? "categorys"
          : from === "learning"
          ? "learning"
          : ""
      }`,
      label: "Learning",
    },
    {
      label: categoriesName,
    },
  ];
  const [showFilterDialog, setShowFilterDialog] = useState(null);
  const open = Boolean(showFilterDialog);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [sortBy, setSortBy] = useState("Last Added");

  const contentTypes = [
    ...new Set(exploreAllData?.map((item) => item.content_type)),
  ];

  const filteredAndSortedData = useMemo(() => {
    let filtered = [...exploreAllData];

    if (selectedDurations?.length) {
      filtered = filtered?.filter((card) => {
        const cardMinutes = getMinutes(card.duration);
        return selectedDurations?.some((d) => {
          const limit = parseInt(d.replace(/[^0-9]/g, ""), 10);

          return cardMinutes < limit;
        });
      });
    }

    if (selectedTypes.length) {
      filtered = filtered?.filter((card) =>
        selectedTypes.includes(card?.content_type)
      );
    }

    if (sortBy === "A - Z") {
      filtered?.sort((a, b) => a?.title.localeCompare(b.title));
    } else if (sortBy === "Z - A") {
      filtered?.sort((a, b) => b?.title.localeCompare(a.title));
    } else {
      filtered?.sort((a, b) => b.id - a.id);
    }

    return filtered;
  }, [exploreAllData, selectedDurations, selectedTypes, sortBy]);

  const toggleSelection = (item, selected, setSelected) => {
    setSelected(
      selected.includes(item)
        ? selected.filter((i) => i !== item)
        : [...selected, item]
    );
  };

  if (exploreAllDataLoader) {
    return <VSNLoader variant="transParent" />;
  }

  return (
    <Box p={{ sm: 1, md: 3 }}>
      <VSNBreadcrumbs
        {...{ breadcrumbsRoutes }}
        sx={{ padding: { sm: "8px 0px 0px 8px", md: "0px" } }}
      />

      <Box>
        <Box py={2} Box sx={{ position: "relative" }}>
          <Stack
            direction={{ sm: "column", md: "row" }}
            justifyContent="space-between"
            className={classes.sliderHeader}
            px={2}
            gap={2}
          >
            <Box>
              <VSNTypography variant="h2">{categoriesName ?? ""}</VSNTypography>
              {/* <VSNTypography variant="h5">
                Discover the science of sleep and how to make it work for you.
                Explore habits, tools, and tips for better sleep.
              </VSNTypography> */}
            </Box>

            {exploreAllData?.length > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={2}
              >
                <InputLabel>Sort By</InputLabel>
                <FormControl size="small">
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    {sortOptions?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton
                  className={classes.filterIcon}
                  onClick={(e) => setShowFilterDialog(e.currentTarget)}
                >
                  <FilterAltIcon />
                </IconButton>
                <Menu
                  open={open}
                  anchorEl={showFilterDialog}
                  onClose={() => setShowFilterDialog(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        border: `1px solid #CBCBCF`,
                        borderRadius: 4,
                        marginTop: 1,
                        minWidth: 400,
                        "& .MuiList-root": {
                          padding: 0,
                        },
                      },
                    },
                  }}
                >
                  <Box p={2}>
                    {/* <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography variant="subtitle2" color="#898994">
                        Duration
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
                      {durations.map((d) => (
                        <Chip
                          key={d}
                          label={d}
                          clickable
                          sx={{ borderRadius: 2 }}
                          variant={
                            selectedDurations.includes(d)
                              ? "default"
                              : "outlined"
                          }
                          color={
                            selectedDurations.includes(d)
                              ? "primary"
                              : "default"
                          }
                          onClick={() =>
                            toggleSelection(
                              d,
                              selectedDurations,
                              setSelectedDurations
                            )
                          }
                        />
                      ))}
                    </Stack> */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography variant="subtitle2" color="#898994">
                        Content Type
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
                      {contentTypes.map((t) => (
                        <Chip
                          key={t}
                          label={capitalizeFirstLetter(t)}
                          sx={{ borderRadius: 2 }}
                          variant={
                            selectedTypes.includes(t) ? "default" : "outlined"
                          }
                          clickable
                          color={
                            selectedTypes.includes(t) ? "primary" : "default"
                          }
                          onClick={() =>
                            toggleSelection(t, selectedTypes, setSelectedTypes)
                          }
                        />
                      ))}
                    </Stack>

                    {/* <VSNTypography
                    onClick={() => setShowFilterDialog(null)}
                    color="primary"
                    sx={{ cursor: "pointer" }}
                  >
                    Apply Filter
                  </VSNTypography> */}
                  </Box>
                </Menu>
              </Stack>
            )}
          </Stack>

          <Grid container px={1}>
            {filteredAndSortedData?.length > 0 ? (
              filteredAndSortedData?.map((el, index) => {
                const { icon, label, content, samllIcon } =
                  getContentTypeDetails(el?.content_type?.toLowerCase());
                return (
                  <Grid
                    item
                    xlg={3}
                    lg={4}
                    smd={6}
                    xs={12}
                    key={index}
                    px={1}
                    onClick={() => {
                      if (el?.is_course) {
                        navigate(
                          `/learning/content/${el?.content_type?.toLowerCase()}/${
                            el?.id
                          }?from=${from}&name=${categoriesName}`
                        );
                      } else {
                        navigate(
                          `/learning/content/${el?.content_type?.toLowerCase()}/${
                            el?.id
                          }/${
                            el?.lesson_id
                          }?from=${from}&name=${categoriesName}`
                        );
                      }
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <VSNPaper
                      className={`${classes.videosBox} ${classes.zoomVideoBox} ${content}`}
                      maxHeight="250px"
                    >
                      <VSNProgressiveImage
                        placeholder={videoImage}
                        src={el?.thumbnail}
                        alt="Explore all image"
                        style={{
                          objectFit: "cover",
                          minHeight: "250px",
                          height: "100%",
                          borderRadius: "10px 10px 0 0",
                        }}
                      />
                      <Box className={classes.typeIcon}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                          }}
                        >
                          {icon}
                          <VSNTypography fontSize="12px" fontWeight="500">
                            {content}
                          </VSNTypography>
                        </Box>
                      </Box>
                      <Stack className={classes.zoomVideoBoxInfo}>
                        <VSNTypography
                          variant="body1"
                          color="white.main"
                          textAlign="left"
                          className={classes.contentHead}
                        >
                          {el?.title}
                        </VSNTypography>
                        <VSNTypography
                          className={classes.subText}
                          dangerouslySetInnerHTML={{
                            __html: el?.summary,
                          }}
                        />
                        <VSNTypography
                          className={classes.partnerLogo}
                          dangerouslySetInnerHTML={{
                            __html: el?.partner_logo,
                          }}
                        />
                        <Stack
                          direction="row"
                          className={classes.btnWrap}
                          alignItems="center"
                        >
                          <VSNTypography
                            variant="body2"
                            color="white.main"
                            textAlign="left"
                            sx={{
                              textDecoration: "underline",
                            }}
                          >
                            <span>{samllIcon}</span>
                            {label}
                          </VSNTypography>

                          {/* {showBookmark && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              onBookmark?.(e, el?.course_id, el);
                            }}
                          >
                            {bookmarkedIds.includes(el?.course_id) ? (
                              <RemoveCircleIcon />
                            ) : (
                              <AddCircleIcon />
                            )}
                          </Box>
                        )} */}
                        </Stack>
                      </Stack>
                    </VSNPaper>
                    {/* <Box className={classes.videoBoxInfo}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <VSNTypography variant="body1" color="primary">
                        {el.duration}
                      </VSNTypography>
                      <VSNTypography variant="body2" className={classes.tag}>
                        {el.tag}
                      </VSNTypography>
                    </Stack>
                    <VSNTypography variant="h6" color="#898994">
                      {el.desc}
                    </VSNTypography>
                    <VSNTypography
                      color="primary"
                      variant="h6"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                    >
                      {el.action}
                    </VSNTypography>
                  </Box> */}
                  </Grid>
                );
              })
            ) : (
              <Box
                sx={{
                  margin: "auto",
                  textAlign: "center",
                  position: "relative",
                }}
              >
                <VSNTypography variant="h6" color="textSecondary" mt={6}>
                  No results found for{" "}
                  <span
                    style={{
                      fontWeight: 700,
                    }}
                  >{`${categoriesName ?? ""}`}</span>
                  .
                </VSNTypography>
              </Box>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ExploreAll;
